import React, { useState, useEffect, useCallback } from "react";
import { useHistory, Prompt } from "react-router-dom";
import { useAlert } from "react-alert";
import {
  DndContext, PointerSensor, useSensor, useSensors, DragOverlay, closestCenter
} from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import {
  Box, Tabs, Tab, Button, TextField, Typography, Chip, Paper, CircularProgress,
  Switch, FormControlLabel, Tooltip, Dialog, DialogContent, DialogActions,
  IconButton, useMediaQuery
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PublishIcon from "@mui/icons-material/Publish";
import ImportExportIcon from "@mui/icons-material/ImportExport";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import UndoIcon from "@mui/icons-material/Undo";
import Menu from "../../Menu";
import Header from "../../Header";
import FieldPalette from "./FieldPalette";
import Canvas from "./Canvas";
import FieldInspector, { UI } from "./FieldInspector";
import PreviewPane from "./PreviewPane";
import SharePanel from "./SharePanel";
import ImportExportDialog from "./ImportExportDialog";
import { FormsApi, newField, genId, emptyDefinition, FIELD_TYPE_MAP, findReferences, removeReferences } from "../formsUtils";
import { errorMessage } from "../../../services/http";

/** Teto da pilha de desfazer. Cada passo guarda a definição inteira. */
const LIMITE_HISTORICO = 50;

/**
 * Abas visíveis. Lógica, Cálculos e Escore ficam de fora da tela: os editores continuam no
 * código e a definição preserva `logic`, `calculations` e `scoring`, que o motor segue
 * avaliando. Nenhum formulário usa escore configurável hoje — no APA, risco e ASA saem do
 * motor clínico do APA legado, a partir do peso das opções e da chave clínica do campo.
 */
const ABAS = [
  { chave: "construir", label: "Construir" },
  { chave: "previsualizar", label: "Pré-visualizar" },
  { chave: "compartilhar", label: "Compartilhar" },
];

export default function FormBuilder() {
  const alert = useAlert();
  const history = useHistory();
  const formId = history.location.state && history.location.state.formId;

  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ title: "", description: "", status: "draft" });
  const [definition, setDefinition] = useState(emptyDefinition());
  const [currentPageId, setCurrentPageId] = useState("page_1");
  const [selectedFieldId, setSelectedFieldId] = useState(null);
  const [tab, setTab] = useState(0);
  const abaAtual = (ABAS[tab] || ABAS[0]).chave;
  const [saveState, setSaveState] = useState("saved"); // saved | saving | dirty
  const [activeDrag, setActiveDrag] = useState(null);
  const [ioOpen, setIoOpen] = useState(false);

  const [historico, setHistorico] = useState([]);
  // Referência da definição que está no servidor: desfazer até ela volta a "Salvo".
  const [definicaoGravada, setDefinicaoGravada] = useState(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  useEffect(() => {
    if (!formId) { history.push("/forms"); return; }
    FormsApi.get(formId).then(
      (resp) => {
        const data = resp.data;
        setForm({ title: data.title, description: data.description || "", status: data.status });
        const def = (data.version && data.version.definition) || emptyDefinition();
        if (!def.pages || def.pages.length === 0) def.pages = emptyDefinition().pages;
        setDefinition(def);
        setDefinicaoGravada(def);
        setCurrentPageId(def.pages[0].id);
        setLoading(false);
      },
      (error) => { console.log(error); alert.error("Erro ao carregar formulário"); history.push("/forms"); }
    );
    // eslint-disable-next-line
  }, [formId]);

  /* ------------------------------------------------ gravar, histórico, desfazer
   * Não há auto-save: o construtor edita a definição inteira e uma gravação disparada por
   * tecla publica no rascunho o estado de meio-caminho de quem ainda está montando a regra.
   * Quem grava é o botão Salvar, e o Publicar grava antes de publicar.
   */
  const updateDefinition = useCallback((next) => {
    setHistorico((h) => [...h, definition].slice(-LIMITE_HISTORICO));
    setDefinition(next);
    setSaveState("dirty");
  }, [definition]);

  const desfazer = useCallback(() => {
    if (historico.length === 0) return;
    const anterior = historico[historico.length - 1];
    setDefinition(anterior);
    setHistorico(historico.slice(0, -1));
    setSaveState(anterior === definicaoGravada ? "saved" : "dirty");
  }, [historico, definicaoGravada]);

  const gravarRascunho = useCallback(() => {
    setSaveState("saving");
    return FormsApi.saveDraft(formId, definition).then(
      () => { setSaveState("saved"); setDefinicaoGravada(definition); return true; },
      (error) => {
        setSaveState("dirty");
        alert.error(errorMessage(error, "Erro ao salvar o rascunho"), { timeout: 15000 });
        return false;
      }
    );
  }, [formId, definition, alert]);

  // Ctrl/Cmd+Z. Dentro de um campo de texto o desfazer é o do navegador, sobre o que foi
  // digitado ali — sequestrá-lo faria o atalho apagar a edição inteira em vez da palavra.
  useEffect(() => {
    const emCampoDeTexto = (el) =>
      el && (el.isContentEditable || ["INPUT", "TEXTAREA"].includes(el.tagName));
    const onKey = (e) => {
      if (!(e.key === "z" || e.key === "Z") || !(e.ctrlKey || e.metaKey) || e.shiftKey) return;
      if (emCampoDeTexto(e.target)) return;
      e.preventDefault();
      desfazer();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [desfazer]);

  // Recarregar ou fechar a aba não passa pelo <Prompt> do roteador.
  useEffect(() => {
    if (saveState === "saved") return undefined;
    const aviso = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", aviso);
    return () => window.removeEventListener("beforeunload", aviso);
  }, [saveState]);

  /* ------------------------------------------------------------ page utils */
  const currentPage = definition.pages.find((p) => p.id === currentPageId) || definition.pages[0];
  const pageFields = (currentPage.fieldIds || [])
    .map((id) => definition.fields.find((f) => f.id === id))
    .filter(Boolean);

  function addPage() {
    const id = `page_${Date.now()}`;
    const next = { ...definition, pages: [...definition.pages, { id, title: `Página ${definition.pages.length + 1}`, fieldIds: [] }] };
    updateDefinition(next);
    setCurrentPageId(id);
  }
  function deletePage(pageId) {
    if (definition.pages.length <= 1) return;
    const page = definition.pages.find((p) => p.id === pageId);
    const doomed = definition.fields.filter((f) => (page.fieldIds || []).includes(f.id));
    if (!window.confirm(`Excluir "${page.title}" e ${doomed.length} campo(s) dela?`)) return;
    const next = cleanupOrphans({
      ...definition,
      pages: definition.pages.filter((p) => p.id !== pageId),
      fields: definition.fields.filter((f) => !(page.fieldIds || []).includes(f.id)),
    }, doomed);
    updateDefinition(next);
    setCurrentPageId(next.pages[0].id);
  }
  function renamePage(pageId, title) {
    updateDefinition({ ...definition, pages: definition.pages.map((p) => (p.id === pageId ? { ...p, title } : p)) });
  }

  /* ----------------------------------------------------------- field utils */
  function addFieldAt(type, index) {
    const field = newField(type, definition.fields);
    const fields = [...definition.fields, field];
    const fieldIds = [...(currentPage.fieldIds || [])];
    const at = index === undefined || index < 0 ? fieldIds.length : index;
    fieldIds.splice(at, 0, field.id);
    const pages = definition.pages.map((p) => (p.id === currentPage.id ? { ...p, fieldIds } : p));
    updateDefinition({ ...definition, fields, pages });
    setSelectedFieldId(field.id);
  }
  function updateField(updated) {
    updateDefinition({ ...definition, fields: definition.fields.map((f) => (f.id === updated.id ? updated : f)) });
  }
  /**
   * Regras que citam campos apagados: uma condição órfã não fica só "quebrada", ela passa a
   * valer sempre (`is_empty(undefined)` é true) e esconde outro campo para sempre. Por isso
   * oferecemos limpar em vez de só avisar.
   */
  function cleanupOrphans(next, removedFields) {
    const refs = removedFields.reduce((acc, f) => acc.concat([f.id, f.name]), []).filter(Boolean);
    const found = findReferences(next, refs);
    if (found.length === 0) return next;
    const ok = window.confirm(
      `${found.length} regra(s) referenciam o que foi removido:\n\n${found.join("\n")}\n\n` +
      "OK remove essas regras órfãs (uma condição sem o campo passa a valer sempre). " +
      "Cancelar mantém tudo como está, para você ajustar à mão."
    );
    if (!ok) {
      alert.show("Regras órfãs mantidas. Revise Lógica/Escore/Visibilidade antes de publicar.");
      return next;
    }
    return removeReferences(next, refs);
  }
  function deleteField(id) {
    const orig = definition.fields.find((f) => f.id === id);
    if (!orig) return;
    const pages = definition.pages.map((p) => ({ ...p, fieldIds: (p.fieldIds || []).filter((fid) => fid !== id) }));
    updateDefinition(cleanupOrphans(
      { ...definition, fields: definition.fields.filter((f) => f.id !== id), pages },
      [orig]
    ));
    if (selectedFieldId === id) setSelectedFieldId(null);
  }
  function duplicateField(id) {
    const orig = definition.fields.find((f) => f.id === id);
    if (!orig) return;
    // nome único: evita que duas cópias colidam em `name` (buildContext resolveria só a última).
    const existingNames = new Set(definition.fields.map((f) => f.name));
    let copyName = `${orig.name}_copia`;
    let n = 2;
    while (existingNames.has(copyName)) { copyName = `${orig.name}_copia${n}`; n++; }
    const copy = { ...JSON.parse(JSON.stringify(orig)), id: genId("fld"), name: copyName };
    const idx = (currentPage.fieldIds || []).indexOf(id);
    const fieldIds = [...(currentPage.fieldIds || [])];
    fieldIds.splice(idx + 1, 0, copy.id);
    const pages = definition.pages.map((p) => (p.id === currentPage.id ? { ...p, fieldIds } : p));
    updateDefinition({ ...definition, fields: [...definition.fields, copy], pages });
  }

  /* -------------------------------------------------------------- DnD ----- */
  function handleDragStart(event) {
    setActiveDrag(event.active);
  }
  function handleDragEnd(event) {
    setActiveDrag(null);
    const { active, over } = event;
    if (!over) return;

    const isPalette = active.data.current && active.data.current.isPalette;
    const ids = currentPage.fieldIds || [];

    if (isPalette) {
      const type = active.data.current.fieldType;
      let index = ids.length;
      if (over.id !== "page-dropzone") {
        const overIdx = ids.indexOf(over.id);
        if (overIdx >= 0) index = overIdx;
      }
      addFieldAt(type, index);
      return;
    }

    if (active.id !== over.id && over.id !== "page-dropzone") {
      const oldIdx = ids.indexOf(active.id);
      const newIdx = ids.indexOf(over.id);
      if (oldIdx >= 0 && newIdx >= 0) {
        const fieldIds = arrayMove(ids, oldIdx, newIdx);
        const pages = definition.pages.map((p) => (p.id === currentPage.id ? { ...p, fieldIds } : p));
        updateDefinition({ ...definition, pages });
      }
    }
  }

  /* ------------------------------------------------------------ publish --- */
  function handleSave() {
    gravarRascunho().then((ok) => { if (ok) alert.success("Rascunho salvo."); });
  }

  function handlePublish() {
    // Publicar congela o rascunho como versão nova: se a gravação falhar, publicar mesmo
    // assim colocaria no ar a versão anterior sem que ninguém percebesse.
    gravarRascunho().then((ok) => {
      if (!ok) return;
      FormsApi.publish(formId).then(
        () => { setForm((f) => ({ ...f, status: "published" })); alert.success("Formulário publicado!"); },
        (error) => alert.error(errorMessage(error, "Erro ao publicar"), { timeout: 15000 })
      );
    });
  }

  function saveMeta() {
    FormsApi.update(formId, { title: form.title, description: form.description });
  }

  /* ------------------------------------------- integração com o APA legado --
   * Marcador guardado na própria definição. Quando ligado, o envio também grava
   * em tkk_apa_digital.submissions_apa (formato antigo do JotForm). O mapeamento
   * é específico do APA e falha de propósito em outros formulários — por isso é
   * opt-in por formulário. */
  const legacyApa = (definition.integration && definition.integration.legacyApa) || null;
  const legacyApaEnabled = !!legacyApa && legacyApa.enabled === true && legacyApa.version === 1;
  function setLegacyApa(enabled) {
    updateDefinition({
      ...definition,
      integration: { ...(definition.integration || {}), legacyApa: { enabled, version: 1 } },
    });
  }

  if (loading) {
    return (
      <div id="wrapper"><Menu /><div id="page-wrapper" className="gray-bg"><Header />
        <Box sx={{ display: "flex", justifyContent: "center", p: 8 }}><CircularProgress /></Box>
      </div></div>
    );
  }

  const selectedField = definition.fields.find((f) => f.id === selectedFieldId) || null;

  return (
    <div id="wrapper">
      <Prompt when={saveState !== "saved"}
        message="Há alterações não salvas no formulário. Sair mesmo assim?" />
      <Menu />
      <div id="page-wrapper" className="gray-bg">
        <Header />
        {/* barra superior */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 2, background: "#fff", borderBottom: "1px solid #eee" }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => history.push("/forms")}>Voltar</Button>
          <Box sx={{ display: "flex", flexDirection: "column", flex: 1, maxWidth: 460, gap: 0.25 }}>
            <TextField variant="standard" value={form.title} onBlur={saveMeta}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              InputProps={{ style: { fontSize: 18, fontWeight: 600 } }} />
            <TextField variant="standard" placeholder="Descrição (aparece no topo do formulário — deixe vazio para ocultar)"
              value={form.description} onBlur={saveMeta}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              InputProps={{ style: { fontSize: 12 } }} />
          </Box>
          <Chip size="small" label={form.status === "published" ? "Publicado" : "Rascunho"}
            color={form.status === "published" ? "success" : "default"} />
          <SaveBadge state={saveState} />
          <Box sx={{ flex: 1 }} />
          <Tooltip title="Ao enviar, grava também na estrutura legada (tkk_apa_digital.submissions_apa), no formato antigo do JotForm. Ligue somente no formulário do APA: o mapeamento é específico e faz o envio falhar em outros formulários.">
            <FormControlLabel
              sx={{ mr: 1 }}
              control={<Switch size="small" checked={legacyApaEnabled}
                onChange={(e) => setLegacyApa(e.target.checked)} />}
              label={<Typography variant="caption">APA legado</Typography>}
            />
          </Tooltip>
          <Tooltip title={historico.length ? `Desfazer (${historico.length}) — Ctrl+Z` : "Nada a desfazer"}>
            <span>
              <Button variant="outlined" startIcon={<UndoIcon />}
                disabled={historico.length === 0} onClick={desfazer}>
                Desfazer
              </Button>
            </span>
          </Tooltip>
          <Button variant="outlined" startIcon={<ImportExportIcon />} onClick={() => setIoOpen(true)}>
            JSON
          </Button>
          <Button variant="outlined" startIcon={<SaveIcon />}
            disabled={saveState === "saving"} onClick={handleSave}>
            Salvar
          </Button>
          <Button variant="contained" color="primary" startIcon={<PublishIcon />} onClick={handlePublish}>
            Publicar
          </Button>
        </Box>

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2, background: "#fff", borderBottom: "1px solid #eee" }}>
          {ABAS.map((aba) => <Tab key={aba.chave} label={aba.label} />)}
        </Tabs>

        <Box sx={{ p: 2 }}>
          {abaAtual === "construir" && (
            <DndContext sensors={sensors} collisionDetection={closestCenter}
              onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
              <Box sx={{ display: "flex", gap: 2, alignItems: "stretch", height: "calc(100vh - 180px)" }}>
                <Paper variant="outlined" sx={{ p: 1.5, width: 210, flexShrink: 0, overflow: "auto" }}>
                  <FieldPalette onAdd={(type) => addFieldAt(type)} />
                </Paper>
                <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 0, overflow: "auto" }}>
                  <Canvas
                    pages={definition.pages} currentPageId={currentPage.id}
                    onChangePage={setCurrentPageId} onAddPage={addPage} onDeletePage={deletePage} onRenamePage={renamePage}
                    fields={pageFields} selectedFieldId={selectedFieldId}
                    onSelectField={setSelectedFieldId} onDeleteField={deleteField} onDuplicateField={duplicateField}
                  />
                </Paper>
              </Box>
              <DragOverlay dropAnimation={{ duration: 200, easing: "cubic-bezier(0.2, 0, 0, 1)" }}>
                {activeDrag && activeDrag.data.current && activeDrag.data.current.isPalette ? (
                  <Paper sx={{ p: 1, px: 1.5, background: "#3f51b5", color: "#fff", borderRadius: 1, boxShadow: "0 10px 28px rgba(0,0,0,0.25)" }}>
                    {(FIELD_TYPE_MAP[activeDrag.data.current.fieldType] || {}).label}
                  </Paper>
                ) : activeDrag ? (
                  <FieldDragPreview field={definition.fields.find((f) => f.id === activeDrag.id)} />
                ) : null}
              </DragOverlay>
            </DndContext>
          )}

          {abaAtual === "previsualizar" && (
            <PreviewPane definition={definition} title={form.title} description={form.description} />
          )}
          {abaAtual === "compartilhar" && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <SharePanel formId={formId} published={form.status === "published"} />
            </Paper>
          )}
        </Box>

        <FieldDialog
          field={selectedField} allFields={definition.fields} definition={definition}
          onChange={updateField} onClose={() => setSelectedFieldId(null)}
        />

        <ImportExportDialog
          open={ioOpen}
          onClose={() => setIoOpen(false)}
          definition={definition}
          formTitle={form.title}
          formDescription={form.description}
          onImport={(def, meta) => {
            updateDefinition(def);
            if (def.pages && def.pages[0]) setCurrentPageId(def.pages[0].id);
            setSelectedFieldId(null);
            if (meta && (meta.title !== undefined || meta.description !== undefined)) {
              const nextTitle = meta.title !== undefined ? meta.title : form.title;
              const nextDescription = meta.description !== undefined ? meta.description : form.description;
              setForm((f) => ({ ...f, title: nextTitle, description: nextDescription }));
              FormsApi.update(formId, { title: nextTitle, description: nextDescription });
            }
          }}
        />
      </div>
    </div>
  );
}

/**
 * Propriedades do campo em modal. Substituiu a coluna fixa de 320px: a tela do construtor
 * ficava com três colunas estreitas e o editor, que é o que tem mais conteúdo, era o mais
 * espremido dos três.
 *
 * Fechar não descarta nada: a alteração fica no construtor, e quem grava é o botão Salvar da
 * barra de cima. Por isso Esc e clique no fundo fecham sem perguntar.
 */
function FieldDialog({ field, allFields, definition, onChange, onClose }) {
  const semMovimento = useMediaQuery("(prefers-reduced-motion: reduce)");
  const meta = (field && FIELD_TYPE_MAP[field.type]) || {};

  return (
    <Dialog
      open={!!field} onClose={onClose} fullWidth maxWidth="md"
      aria-labelledby="titulo-propriedades-campo"
      transitionDuration={semMovimento ? 0 : 180}
      slotProps={{ backdrop: { sx: { background: "rgba(23,23,26,0.55)" } } }}
      PaperProps={{ sx: {
        borderRadius: "14px", maxHeight: "80vh",
        // Sombra e relevo no hover dentro de overlay são pintados sobre o fundo escurecido e
        // piscam a cada passagem do ponteiro. Dentro deste modal, hover é só cor de fundo.
        "& .MuiButton-root:hover": { transform: "none", boxShadow: "none" },
        // Abaixo de 16px o Safari do iOS dá zoom ao focar o campo, e não desfaz.
        "@media (max-width: 1023px)": { "& .MuiInputBase-input": { fontSize: 16 } },
      } }}
    >
      {field && (
        <>
          <Box sx={{
            display: "flex", alignItems: "center", gap: 1.5,
            px: 3, py: 2, borderBottom: `1px solid ${UI.borderSubtle}`,
          }}>
            <Box sx={{
              width: 34, height: 34, borderRadius: `${UI.radius}px`, flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              background: UI.primarySoft, border: `1px solid ${UI.primaryBorder}`,
            }}>
              <i className={meta.icon} style={{ color: UI.primary, fontSize: 14 }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography id="titulo-propriedades-campo" variant="subtitle1"
                sx={{ fontWeight: 600, lineHeight: 1.3 }} noWrap>
                {field.label || "(sem rótulo)"}
              </Typography>
              <Typography variant="caption" sx={{ color: UI.textMuted }}>
                {meta.label}{field.name ? ` · ${field.name}` : ""}
              </Typography>
            </Box>
            <IconButton onClick={onClose} aria-label="Fechar"><CloseIcon /></IconButton>
          </Box>

          <DialogContent dividers={false} sx={{ px: 3, py: 2.5, overflowY: "auto" }}>
            <FieldInspector field={field} allFields={allFields}
              definition={definition} onChange={onChange} />
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${UI.borderSubtle}`, gap: 1 }}>
            <Typography variant="caption" sx={{ color: UI.textMuted, flex: 1 }}>
              Use Salvar, na barra de cima, para gravar as alterações.
            </Typography>
            <Button variant="contained" disableElevation onClick={onClose}>Concluir</Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}

function SaveBadge({ state }) {
  const map = {
    saved: { label: "Salvo", color: "#2e7d32" },
    saving: { label: "Salvando...", color: "#ed6c02" },
    dirty: { label: "Não salvo", color: "#b54708" },
  };
  const cfg = map[state] || map.saved;
  return <Typography variant="caption" sx={{ color: cfg.color }}>{cfg.label}</Typography>;
}

/** Card "flutuante" mostrado enquanto se arrasta um campo (DragOverlay). */
function FieldDragPreview({ field }) {
  if (!field) return null;
  const meta = FIELD_TYPE_MAP[field.type] || {};
  return (
    <Paper elevation={8} sx={{
      display: "flex", alignItems: "center", gap: 1, p: 1.5,
      width: 560, maxWidth: "80vw", background: "#fff", borderRadius: 1,
      border: "1px solid #3f51b5", cursor: "grabbing",
      transform: "rotate(-1.5deg)", boxShadow: "0 12px 30px rgba(0,0,0,0.22)",
    }}>
      <i className={meta.icon} style={{ color: "#3f51b5", fontSize: 13 }} />
      <Typography variant="body1" sx={{ fontWeight: 500 }} noWrap>
        {field.label || "(sem rótulo)"}
      </Typography>
    </Paper>
  );
}
